import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale, ShieldCheck, BrainCircuit, FileText, Check, Clock, Send, Loader2,
  MessageSquare, Download, Mail, ChevronDown, Users, Star, Quote,
  Zap, ArrowRight, AlertCircle, Timer, TrendingUp, BookOpen,
  Mic, PaperclipIcon, RefreshCw, CheckCircle2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

import { BACKEND_URL } from '../config';

// KAI Avatar — używa tego samego awatara co panel użytkownika
function KaiAvatar({ size = 96, pulse = false, speaking = false }) {
  return (
    <div className="relative inline-flex flex-shrink-0" style={{ width: size, height: size }}>
      {pulse && (
        <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-25 animate-ping" />
      )}
      <div
        className="relative w-full h-full rounded-full overflow-hidden shadow-lg bg-white"
        style={{ border: speaking ? '3px solid #34d399' : '3px solid rgba(255,255,255,0.3)' }}
      >
        <img
          src="/images/kai_avatar.jpeg"
          alt="KAI"
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center top' }}
          draggable={false}
        />
      </div>
      {speaking && (
        <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white flex items-center justify-center">
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
        </span>
      )}
    </div>
  );
}

// Typing indicator
function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const [demoQuestion, setDemoQuestion] = useState('');
  const [demoMessages, setDemoMessages] = useState([]);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoCount, setDemoCount] = useState(() => {
    const saved = localStorage.getItem('kai_demo_count');
    return saved ? parseInt(saved) : 0;
  });
  const maxDemo = 3;
  const [leadEmail, setLeadEmail] = useState('');
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [publicStats, setPublicStats] = useState({ user_count: 0, question_count: 0 });
  const [kaiSpeaking, setKaiSpeaking] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/stats/public`).then(r => setPublicStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (demoMessages.length > 0 && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [demoMessages, demoLoading]);

  const handleDemoAsk = async (e) => {
    e.preventDefault();
    if (!demoQuestion.trim() || demoLoading || demoCount >= maxDemo) return;
    const question = demoQuestion.trim();
    setDemoMessages(prev => [...prev, { role: 'user', content: question }]);
    setDemoQuestion('');
    setDemoLoading(true);
    setKaiSpeaking(false);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/demo/chat`, { content: question });
      setDemoMessages(prev => [...prev, { role: 'assistant', content: response.data.content }]);
      const newCount = demoCount + 1;
      setDemoCount(newCount);
      localStorage.setItem('kai_demo_count', String(newCount));
      setKaiSpeaking(true);
      setTimeout(() => setKaiSpeaking(false), 3000);
    } catch {
      setDemoMessages(prev => [...prev, { role: 'assistant', content: 'Przepraszam, wystąpił błąd. Zarejestruj się aby korzystać z pełnej wersji KAI.' }]);
    } finally {
      setDemoLoading(false);
    }
  };

  const handleQuickQuestion = async (q) => {
    if (demoCount >= maxDemo || demoLoading) return;
    setDemoMessages(prev => [...prev, { role: 'user', content: q }]);
    setDemoLoading(true);
    setKaiSpeaking(false);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/demo/chat`, { content: q });
      setDemoMessages(prev => [...prev, { role: 'assistant', content: response.data.content }]);
      const newCount = demoCount + 1;
      setDemoCount(newCount);
      localStorage.setItem('kai_demo_count', String(newCount));
      setKaiSpeaking(true);
      setTimeout(() => setKaiSpeaking(false), 3000);
    } catch {
      setDemoMessages(prev => [...prev, { role: 'assistant', content: 'Przepraszam, wystąpił błąd. Zarejestruj się aby korzystać z pełnej wersji KAI.' }]);
    } finally {
      setDemoLoading(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadEmail.trim() || leadLoading) return;
    setLeadLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, { email: leadEmail.trim() });
      setLeadSubmitted(true);
      window.open(`${BACKEND_URL}/api/leads/guide`, '_blank');
    } catch {
      setLeadSubmitted(true);
      window.open(`${BACKEND_URL}/api/leads/guide`, '_blank');
    } finally {
      setLeadLoading(false);
    }
  };

  const quickQuestions = [
    'Jaki jest limit zwolnienia z VAT w 2026 r.?',
    'Jak rozliczyć fakturę korygującą?',
    'Kiedy stosować split payment?',
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* ─── NAVBAR ──────────────────────────────────────────────── */}
      <nav className="absolute top-0 left-0 right-0 z-50">
        <div className="w-full px-0 py-0 flex items-start justify-between">
          <div className="flex items-center" style={{ marginLeft: '2.5rem', marginTop: '2.5rem' }}>
            <div className="rounded-2xl flex items-center justify-center overflow-hidden" style={{ width: '12.75rem', height: '12.75rem', boxShadow: '4px 6px 12px rgba(0,0,0,0.35), -2px -2px 6px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
              <img src="/kai-logo.png" alt="KAI" className="w-full h-full object-cover rounded-2xl" data-testid="landing-logo" />
            </div>
          </div>
          <div className="flex items-center gap-4" style={{ marginTop: '4rem', marginRight: 'auto', marginLeft: '55%' }}>
            <button
              onClick={() => navigate('/auth')}
              className="text-base text-emerald-200/90 hover:text-white transition-colors font-semibold px-5 py-2.5"
              data-testid="nav-login-btn"
            >
              Zaloguj sie
            </button>
            <Button
              onClick={() => navigate('/auth')}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-full px-8 py-5 text-base"
              data-testid="nav-register-btn"
            >
              Wyprobuj za darmo
            </Button>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-slate-900">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1645102270210-e47d263db8c4)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-primary-950/60" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-28 lg:pt-32 pb-20 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left: Copy */}
            <div>
              <h1
                data-testid="hero-heading"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight tracking-tight"
              >
                Ekspert Księgowy AI —{' '}
                <span className="text-emerald-400">zawsze pod ręką</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 mb-4 leading-relaxed">
                KAI odpowiada na pytania z polskiego prawa podatkowego i rachunkowego
                w&nbsp;kilka sekund — z&nbsp;podstawą prawną i&nbsp;cytatem przepisu.
              </p>

              <ul className="space-y-2 mb-8">
                {[
                  'Odpowiedź z podstawą prawną — nie tylko opis',
                  'Wiedza z branżowych, rzetelnych i sprawdzonych źródeł',
                  'Obsługuje KSeF 2.0, JPK, VAT, PIT, CIT i wiele więcej',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-slate-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="cta-start-button"
                  onClick={() => navigate('/auth')}
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-8 py-4 text-base rounded-xl shadow-lg shadow-emerald-900/40 hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 gap-2"
                >
                  Zacznij 7 dni za darmo <ArrowRight className="w-5 h-5" />
                </Button>
                <Button
                  data-testid="learn-more-button"
                  onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                  variant="ghost"
                  size="lg"
                  className="border border-white/20 text-white/80 hover:bg-white/10 font-semibold px-8 py-4 text-base rounded-xl transition-all duration-200"
                >
                  Wypróbuj bez rejestracji
                </Button>
              </div>

              <p className="text-xs text-slate-400 mt-4">Bez karty kredytowej · Anuluj kiedy chcesz · Faktura VAT</p>
            </div>

            {/* Right: KAI Avatar + floating chat bubbles */}
            <div className="hidden lg:flex flex-col items-center justify-center relative">
              {/* Glow */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-80 h-80 rounded-full bg-emerald-500/10 blur-3xl" />
              </div>

              {/* Avatar container */}
              <div className="relative z-10 flex flex-col items-center">
                {/* Floating chat bubble top */}
                <div className="mb-4 self-start ml-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl rounded-bl-sm px-4 py-3 max-w-xs animate-bounce" style={{ animationDuration: '3s' }}>
                  <p className="text-white text-sm font-medium">Jak rozliczyć najem prywatny w 2026 r.?</p>
                </div>

            <KaiAvatar size={200} pulse speaking={kaiSpeaking} />

                <div className="mt-2 px-5 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
                  <p className="text-emerald-300 text-sm font-semibold">KAI — Twój asystent księgowy</p>
                </div>

                {/* Floating chat bubble bottom */}
                <div className="mt-4 self-end mr-8 bg-emerald-900/60 backdrop-blur-sm border border-emerald-500/30 rounded-2xl rounded-br-sm px-4 py-3 max-w-xs">
                  <p className="text-emerald-100 text-sm leading-relaxed">
                    Najem prywatny w 2026 r. rozliczasz wyłącznie ryczałtem <span className="font-semibold text-emerald-300">8,5% / 12,5%</span>.
                    Podstawa: art. 6 ust. 1a ustawy o ryczałcie (Dz.U. 2021 poz. 1993).
                  </p>
                </div>
              </div>

              {/* Ring decorations */}
              <div className="absolute top-4 right-4 w-24 h-24 rounded-full border border-emerald-500/20 z-0" />
              <div className="absolute bottom-4 left-4 w-16 h-16 rounded-full border border-white/10 z-0" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─────────────────────────────────────────────── */}
      <div className="bg-primary-900 border-b border-primary-800" data-testid="user-counter-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-wrap items-center justify-center gap-6 sm:gap-12">
          {[
            { icon: <Users className="w-5 h-5 text-emerald-400" />, value: `${publicStats.user_count}+`, label: 'Zarejestrowanych księgowych', test: 'user-count' },
            { icon: <MessageSquare className="w-5 h-5 text-blue-400" />, value: `${publicStats.question_count}+`, label: 'Udzielonych odpowiedzi', test: 'question-count' },
            { icon: <Clock className="w-5 h-5 text-amber-400" />, value: '24/7', label: 'Dostępność asystenta' },
            { icon: <Timer className="w-5 h-5 text-rose-400" />, value: '<10 s', label: 'Czas odpowiedzi' },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">{s.icon}</div>
              <div>
                <p className="text-xl font-bold text-white" data-testid={s.test}>{s.value}</p>
                <p className="text-xs text-emerald-200/70">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── PAIN POINTS ───────────────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl text-primary-900 mb-3">
              Znasz to uczucie?
            </h2>
            <p className="text-muted-foreground text-lg">Każdy doświadczony księgowy wie, jak cenny jest czas</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <AlertCircle className="w-7 h-7 text-red-500" />,
                bg: 'bg-red-50 border-red-100',
                problem: 'Godziny szukania przepisu',
                desc: 'Przekopujesz dziesiątki stron, portali i interpretacji tylko po to, by znaleźć jedną odpowiedź.',
                fix: 'KAI odpowiada w 10 sekund z cytowanym przepisem',
              },
              {
                icon: <RefreshCw className="w-7 h-7 text-amber-500" />,
                bg: 'bg-amber-50 border-amber-100',
                problem: 'Zmiany przepisów co miesiąc',
                desc: 'Śledzenie nowelizacji ustaw, rozporządzeń i interpretacji to osobna praca na pełen etat.',
                fix: 'Baza wiedzy KAI jest stale aktualizowana',
              },
              {
                icon: <Timer className="w-7 h-7 text-blue-500" />,
                bg: 'bg-blue-50 border-blue-100',
                problem: 'Klient czeka na odpowiedź',
                desc: 'Każda chwila wahania podważa zaufanie. Profesjonalizm wymaga szybkości i pewności.',
                fix: 'Pewna odpowiedź z podstawą prawną od ręki',
              },
            ].map((p, i) => (
              <div key={i} className={`rounded-2xl border p-6 ${p.bg}`}>
                <div className="mb-3">{p.icon}</div>
                <h3 className="font-bold text-slate-800 mb-2">{p.problem}</h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">{p.desc}</p>
                <div className="flex items-start gap-2 pt-3 border-t border-black/5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs font-semibold text-emerald-700">{p.fix}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES ──────────────────────────────────────────────── */}
      <section id="features" className="py-20 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-primary-900 mb-4">
              Wszystko czego potrzebuje nowoczesna księgowość
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Zaawansowane funkcje zaprojektowane specjalnie dla polskich biur rachunkowych i księgowych
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <BrainCircuit className="w-10 h-10 text-primary-500" />,
                title: 'Zaawansowana AI',
                description: 'Sieć modeli AI najnowszej generacji — wybieramy najlepszy model dla każdego pytania',
              },
              {
                icon: <Scale className="w-10 h-10 text-primary-500" />,
                title: 'Podstawy Prawne',
                description: 'Każda odpowiedź zawiera cytowany artykuł, ustęp i numer Dziennika Ustaw',
              },
              {
                icon: <FileText className="w-10 h-10 text-primary-500" />,
                title: 'Twoja Baza Wiedzy',
                description: 'Dodaj własne dokumenty, umowy i procedury — KAI odpowiada w ich kontekście',
              },
              {
                icon: <ShieldCheck className="w-10 h-10 text-primary-500" />,
                title: 'Rzetelne Źródła',
                description: 'Tylko sprawdzone, branżowe i aktualizowane źródła wiedzy — żadnych niesprawdzonych porad',
              },
              {
                icon: <Mic className="w-10 h-10 text-primary-500" />,
                title: 'Rozmowa Głosowa',
                description: 'Dyktuj pytania i słuchaj odpowiedzi — idealne podczas pracy z dokumentami',
              },
              {
                icon: <PaperclipIcon className="w-10 h-10 text-primary-500" />,
                title: 'Analiza Faktur',
                description: 'Wgraj zdjęcie faktury — KAI odczyta dane, zweryfikuje VAT i doradzi jak zaksięgować',
              },
              {
                icon: <TrendingUp className="w-10 h-10 text-primary-500" />,
                title: 'Kalkulatory Podatkowe',
                description: 'Kalkulator składek ZUS, podatku liniowego, ryczałtu i porównania form opodatkowania',
              },
              {
                icon: <BookOpen className="w-10 h-10 text-primary-500" />,
                title: 'KSeF i JPK',
                description: 'Pełna integracja z KSeF 2.0 — wystawianie, pobieranie i weryfikacja e-faktur',
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                data-testid={`feature-card-${idx}`}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md hover:border-primary-200 transition-all duration-300 hover:-translate-y-0.5 group"
              >
                <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="font-bold text-base text-primary-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ──────────────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl text-primary-900 mb-3">Jak to działa?</h2>
            <p className="text-muted-foreground text-lg">Trzy proste kroki do profesjonalnej odpowiedzi</p>
          </div>
          <div className="relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-10 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-200 via-blue-200 to-primary-200 mx-24" />
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: '01', icon: <MessageSquare className="w-6 h-6" />, color: 'bg-emerald-500', title: 'Zadaj pytanie', desc: 'Wpisz pytanie po polsku — możesz też wgrać fakturę, zdjęcie lub dokument' },
                { step: '02', icon: <BrainCircuit className="w-6 h-6" />, color: 'bg-blue-500', title: 'KAI analizuje', desc: 'AI przeszukuje rzetelne, branżowe źródła wiedzy i aktualną bazę przepisów' },
                { step: '03', icon: <Scale className="w-6 h-6" />, color: 'bg-primary-600', title: 'Odpowiedź + przepis', desc: 'Otrzymujesz precyzyjną odpowiedź z cytowaną podstawą prawną' },
              ].map((s, i) => (
                <div key={i} className="flex flex-col items-center text-center">
                  <div className={`w-20 h-20 ${s.color} rounded-2xl flex items-center justify-center text-white shadow-lg mb-4 z-10 relative`}>
                    {s.icon}
                  </div>
                  <span className="text-xs font-bold text-muted-foreground tracking-widest mb-1">{s.step}</span>
                  <h3 className="font-bold text-lg text-primary-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── DEMO CHAT ─────────────────────────────────────────────── */}
      <section id="demo" className="py-20 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl sm:text-4xl text-primary-900 mb-3">
              Porozmawiaj z KAI — teraz, za darmo
            </h2>
            <p className="text-muted-foreground">
              Bez rejestracji · {maxDemo - demoCount} z {maxDemo} pytań pozostało
            </p>
          </div>

          <div data-testid="demo-chat" className="bg-white border-2 border-slate-200 rounded-2xl shadow-xl overflow-hidden">
            {/* Chat header with KAI avatar */}
            <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-primary-900 to-slate-900 border-b border-white/10">
              <KaiAvatar size={42} speaking={kaiSpeaking} />
              <div>
                <p className="font-bold text-white text-sm">KAI — Asystent Księgowy</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-xs text-emerald-300">Online · odpowiada w &lt;10 sek.</span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="min-h-[240px] max-h-[380px] overflow-y-auto p-5 space-y-4 bg-slate-50/50">
              {demoMessages.length === 0 && (
                <div className="space-y-3">
                  {/* KAI welcome message */}
                  <div className="flex items-start gap-3">
                    <KaiAvatar size={32} />
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm max-w-[85%]">
                      <p className="text-sm text-slate-700">
                        Cześć! Jestem <strong>KAI</strong> — Twój AI asystent księgowy. 👋<br />
                        Zadaj mi pytanie o polskie prawo podatkowe, a odpowiem z podstawą prawną.
                      </p>
                    </div>
                  </div>
                  {/* Quick questions */}
                  <div className="pl-11 space-y-2">
                    <p className="text-xs text-slate-400 font-medium">Popularne pytania:</p>
                    {quickQuestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => handleQuickQuestion(q)}
                        disabled={demoCount >= maxDemo || demoLoading}
                        className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        💬 {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {demoMessages.map((msg, i) => (
                <div key={i} className={`flex items-start gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && <KaiAvatar size={32} />}
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-primary-900 text-white rounded-tr-sm'
                      : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}

              {demoLoading && (
                <div className="flex items-start gap-3">
                  <KaiAvatar size={32} speaking />
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm shadow-sm">
                    <TypingDots />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-slate-200 p-4 bg-white">
              {demoCount >= maxDemo ? (
                <div className="text-center py-2">
                  <p className="text-sm text-slate-600 mb-3">Wykorzystano limit pytań demo — zarejestruj się, by korzystać bez ograniczeń</p>
                  <Button onClick={() => navigate('/auth')} className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold gap-2">
                    Zacznij 7 dni za darmo <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleDemoAsk} className="flex gap-2">
                  <Input
                    data-testid="demo-input"
                    value={demoQuestion}
                    onChange={(e) => setDemoQuestion(e.target.value)}
                    placeholder="Zadaj pytanie o prawo podatkowe..."
                    disabled={demoLoading}
                    className="flex-1 rounded-xl"
                  />
                  <Button
                    type="submit"
                    disabled={demoLoading || !demoQuestion.trim()}
                    className="bg-primary-900 hover:bg-primary-800 text-white px-5 rounded-xl"
                  >
                    {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-4">
            Demo prezentuje możliwości KAI · Pełna wersja zawiera więcej funkcji i nieograniczone pytania
          </p>
        </div>
      </section>

      {/* ─── SOCIAL PROOF / TESTIMONIALS ──────────────────────────── */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-primary-50 via-white to-emerald-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl text-primary-900 mb-4">
              Zaufali nam polscy księgowi
            </h2>
            <p className="text-lg text-muted-foreground">Opinie praktyków z biur rachunkowych i samodzielnych księgowych</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Anna K.',
                role: 'Główna księgowa, biuro rachunkowe',
                text: 'KAI oszczędza mi co najmniej godzinę dziennie. Zamiast przekopywać portale, zadaję pytanie i dostaję odpowiedź z podstawą prawną. Bezcenne narzędzie.',
                saving: '~5 godz./tydzień',
                rating: 5,
              },
              {
                name: 'Marek W.',
                role: 'Właściciel biura rachunkowego',
                text: 'Moduł Specjalistów AI to strzał w dziesiątkę. Ekspert VAT pomaga mi z trudnymi przypadkami split payment i KSeF. Rekomenduje każdemu biuru rachunkowemu.',
                saving: 'Szybsze konsultacje',
                rating: 5,
              },
              {
                name: 'Katarzyna P.',
                role: 'Samodzielna księgowa',
                text: 'Audytor faktur i tłumacz przepisów to funkcje, których szukałam od dawna. Teraz mogę szybko zweryfikować fakturę albo wytłumaczyć klientowi zmianę w przepisach.',
                saving: 'Pewniejsze odpowiedzi',
                rating: 5,
              },
            ].map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid={`testimonial-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1">
                    {[...Array(t.rating)].map((_, s) => (
                      <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">{t.saving}</span>
                </div>
                <div className="relative mb-4">
                  <Quote className="w-7 h-7 text-primary-100 absolute -top-1 -left-1" />
                  <p className="text-sm text-slate-600 leading-relaxed pl-5 italic">{t.text}</p>
                </div>
                <div className="pt-4 border-t border-slate-100">
                  <p className="font-semibold text-sm text-primary-900">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            {[
              { icon: <ShieldCheck className="w-5 h-5 text-emerald-600" />, text: 'Bezpieczne i szyfrowane dane' },
              { icon: <Scale className="w-5 h-5 text-blue-600" />, text: 'Zgodność z RODO' },
              { icon: <FileText className="w-5 h-5 text-primary-600" />, text: 'Faktura VAT po każdej płatności' },
              { icon: <Clock className="w-5 h-5 text-amber-600" />, text: '7 dni bezpłatnie, bez karty kredytowej' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full shadow-sm">
                {b.icon}
                <span className="text-sm text-slate-700 font-medium">{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ───────────────────────────────────────────────── */}
      <section id="pricing" className="py-24 px-6 lg:px-12 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold mb-6">
              <Clock className="w-4 h-4" />
              7 dni bezpłatnego testowania — bez karty
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-primary-900 mb-4">
              Inwestycja, która się zwraca
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Jedna zaoszczędzona godzina tygodniowo zwraca koszt abonamentu już po kilku dniach.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
            {/* Monthly */}
            <div data-testid="plan-monthly" className="bg-white border-2 border-slate-200 rounded-2xl p-7 hover:shadow-lg transition-all hover:-translate-y-0.5">
              <h3 className="font-heading text-xl text-primary-900 mb-1">Miesięczny</h3>
              <p className="text-sm text-muted-foreground mb-5">Elastyczny, bez zobowiązań</p>
              <div className="mb-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-primary-900">55</span>
                  <span className="text-slate-500">zł netto/mies.</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">67,65 zł brutto · VAT 23%</p>
              </div>
              <ul className="space-y-2.5 mb-7 text-sm">
                {[
                  'Pełny dostęp do KAI AI',
                  'Historia bez limitu',
                  'Rozmowa głosowa i analiza plików',
                  'Kalkulatory i KSeF 2.0',
                  'Anulowanie w każdej chwili',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-600">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate('/auth?plan=monthly')}
                variant="outline"
                className="w-full h-12 rounded-xl border-2 border-primary-200 text-primary-900 hover:bg-primary-50 font-semibold"
                data-testid="buy-monthly-btn"
              >
                Zacznij za darmo
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-3">7 dni gratis, potem 67,65 zł/mies.</p>
            </div>

            {/* Half-Yearly */}
            <div data-testid="plan-halfyearly" className="bg-white border-2 border-blue-400 rounded-2xl p-7 hover:shadow-lg transition-all hover:-translate-y-0.5 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                OSZCZĘDŹ 7%
              </div>
              <h3 className="font-heading text-xl text-primary-900 mb-1">Półroczny</h3>
              <p className="text-sm text-muted-foreground mb-5">Komfort bez codziennych płatności</p>
              <div className="mb-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-primary-900">306,90</span>
                  <span className="text-slate-500">zł netto/6 mies.</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">377,49 zł brutto · tylko 51,15 zł/mies.</p>
              </div>
              <ul className="space-y-2.5 mb-7 text-sm">
                {[
                  'Wszystko z planu miesięcznego',
                  '7% taniej niż miesięczny',
                  'Faktura VAT do rozliczenia',
                  'Priorytetowe wsparcie email',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-600">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate('/auth?plan=halfyearly')}
                className="w-full h-12 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-bold"
                data-testid="buy-halfyearly-btn"
              >
                Zacznij za darmo
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-3">7 dni gratis, potem 377,49 zł / 6 mies.</p>
            </div>

            {/* Yearly — best value */}
            <div data-testid="plan-yearly" className="bg-gradient-to-b from-primary-900 to-slate-900 border-2 border-primary-700 rounded-2xl p-7 hover:shadow-xl transition-all hover:-translate-y-0.5 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                NAJLEPSZA WARTOŚĆ · OSZCZĘDŹ 15%
              </div>
              <h3 className="font-heading text-xl text-white mb-1">Roczny</h3>
              <p className="text-sm text-emerald-300 mb-5">Dla profesjonalistów</p>
              <div className="mb-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">561</span>
                  <span className="text-slate-300">zł netto/rok</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">690,03 zł brutto · tylko 46,75 zł/mies.</p>
              </div>
              <ul className="space-y-2.5 mb-7 text-sm">
                {[
                  'Wszystko z planu miesięcznego',
                  '15% taniej — oszczędzasz ~99 zł',
                  'Faktura VAT do rozliczenia',
                  'Priorytetowe wsparcie email',
                  'Wczesny dostęp do nowych funkcji',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-200">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate('/auth?plan=yearly')}
                className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-bold"
                data-testid="buy-yearly-btn"
              >
                Zacznij za darmo
              </Button>
              <p className="text-xs text-center text-slate-400 mt-3">7 dni gratis, potem 690,03 zł/rok</p>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Płatności obsługiwane przez Tpay (BLIK, przelewy bankowe, karty). Faktura VAT po każdej płatności.
          </p>
        </div>
      </section>

      {/* ─── LEAD MAGNET ───────────────────────────────────────────── */}
      <div className="py-20 px-6 lg:px-12 bg-gradient-to-r from-primary-950 via-primary-900 to-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-white/80 text-sm mb-6">
            <Download className="w-4 h-4" /> Bezpłatny poradnik
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
            10 najczęstszych błędów księgowych
          </h2>
          <p className="text-lg text-emerald-100 mb-8">
            Pobierz bezpłatny poradnik i dowiedz się jak uniknąć kosztownych pomyłek w rozliczeniach
          </p>
          {leadSubmitted ? (
            <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-6">
              <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-white font-medium">Poradnik został pobrany!</p>
              <p className="text-emerald-200 text-sm mt-1">Sprawdź również swoją skrzynkę email</p>
            </div>
          ) : (
            <form onSubmit={handleLeadSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="lead-form">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  data-testid="lead-email"
                  type="email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  placeholder="Twój adres email..."
                  required
                  className="w-full h-12 pl-11 pr-4 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <Button
                type="submit"
                disabled={leadLoading}
                className="h-12 px-8 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl"
                data-testid="lead-submit"
              >
                {leadLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Pobierz za darmo
              </Button>
            </form>
          )}
          <p className="text-xs text-white/40 mt-4">Bez spamu. Możesz się wypisać w każdej chwili.</p>
        </div>
      </div>

      {/* ─── FAQ (SEO) ─────────────────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl text-primary-900 mb-10 text-center">
            Najczęściej zadawane pytania
          </h2>
          <div className="space-y-3" data-testid="faq-section">
            {[
              {
                q: 'Czym jest KAI i jak pomaga w pracy księgowego?',
                a: 'KAI to asystent AI specjalnie zaprojektowany dla polskich księgowych i biur rachunkowych. Odpowiada na pytania z zakresu podatków VAT, PIT, CIT, ZUS, prawa rachunkowego i KSeF — z podstawą prawną. Działa 24/7 i odpowiada w kilka sekund.',
              },
              {
                q: 'Czy KAI zastąpi mojego księgowego?',
                a: 'Nie. KAI wspiera pracę księgowego — pomaga szybko znaleźć przepis, obliczyć podatek i przygotować wyjaśnienie dla klienta. Nie zastępuje profesjonalnej obsługi księgowej ani nie ponosi odpowiedzialności prawnej.',
              },
              {
                q: 'Skąd KAI czerpie wiedzę?',
                a: 'KAI korzysta wyłącznie ze sprawdzonych, rzetelnych i branżowych źródeł wiedzy — oficjalnych aktów prawnych, Dziennika Ustaw (ISAP), interpretacji podatkowych Krajowej Informacji Skarbowej oraz specjalistycznych źródeł z zakresu prawa podatkowego i rachunkowego. Baza jest stale aktualizowana.',
              },
              {
                q: 'Czy mogę przetestować KAI za darmo?',
                a: 'Tak! Po rejestracji otrzymujesz 7 dni bezpłatnego dostępu do wszystkich funkcji — bez karty kredytowej. Możesz też zadać 3 pytania na stronie głównej bez rejestracji.',
              },
              {
                q: 'Jak KAI obsługuje pytania o KSeF 2.0?',
                a: 'KAI posiada pełny moduł KSeF 2.0: otwieranie sesji interaktywnej, wysyłanie i pobieranie e-faktur, sprawdzanie statusu UPO, klasyfikacja AI faktur na potrzeby księgowe. Obsługuje nowe API KSeF obowiązujące od 2026 r.',
              },
              {
                q: 'Czy KAI analizuje faktury ze zdjęć i PDF?',
                a: 'Tak. Możesz załączyć zdjęcie lub skan faktury, a KAI odczyta dane (NIP, kwoty, daty, stawki VAT), zweryfikuje poprawność i doradzi jak ją zaksięgować zgodnie z obowiązującymi przepisami.',
              },
              {
                q: 'Czy dane i rozmowy są bezpieczne?',
                a: 'Tak. Wszystkie rozmowy są szyfrowane (TLS). Dane przechowywane są na zabezpieczonych serwerach. Nie udostępniamy danych osobom trzecim i działamy zgodnie z RODO. Szczegóły w Polityce Prywatności.',
              },
              {
                q: 'Jakie formy płatności obsługujecie?',
                a: 'Płatności realizuje Tpay — obsługujemy BLIK, przelewy bankowe i karty płatnicze. Po każdej płatności automatycznie wystawiamy fakturę VAT, którą możesz zaliczyć w koszty firmy.',
              },
            ].map((faq, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <span className="font-medium text-primary-900 text-sm pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─────────────────────────────────────────────── */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-primary-950 via-primary-900 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <KaiAvatar size={100} pulse />
          </div>
          <h2 className="font-heading text-3xl sm:text-5xl text-white mb-4">
            Gotowy na szybszą i pewniejszą pracę?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Dołącz do {publicStats.user_count}+ polskich księgowych, którzy już oszczędzają czas dzięki KAI.
            Pierwsze 7 dni całkowicie za darmo — bez karty kredytowej.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              data-testid="cta-bottom-button"
              onClick={() => navigate('/auth')}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-10 py-6 text-lg rounded-xl shadow-lg shadow-emerald-900/40 hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 gap-2"
            >
              Zacznij 7 dni za darmo <ArrowRight className="w-5 h-5" />
            </Button>
            <Button
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              variant="ghost"
              size="lg"
              className="text-white/70 hover:text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl"
            >
              Zobacz plany i ceny
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-6">Bez zobowiązań · Anuluj w każdej chwili · Faktura VAT</p>
        </div>
      </section>

      {/* ─── FOOTER ────────────────────────────────────────────────── */}
      <footer className="bg-primary-950 text-emerald-100 py-10 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/kai-logo.png" alt="KAI" className="w-12 h-12 object-contain" />
              <span className="font-heading font-bold text-white">KAI</span>
              <span className="text-sm text-emerald-200/60 ml-2">&copy; 2026 Kurdynowski AI</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="/terms" className="text-emerald-200/80 hover:text-white transition-colors" data-testid="footer-terms-link">Regulamin</a>
              <a href="/privacy" className="text-emerald-200/80 hover:text-white transition-colors" data-testid="footer-privacy-link">Polityka Prywatności</a>
              <a href="/copyright" className="text-emerald-200/80 hover:text-white transition-colors" data-testid="footer-copyright-link">Prawa Autorskie</a>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-emerald-100/10 text-center">
            <p className="text-xs text-emerald-200/50">
              Asystent AI dla polskich księgowych i biur rachunkowych. Odpowiedzi z podstawą prawną, KSeF 2.0, JPK, analiza faktur.
              Kontakt: monika.gawkowska@kurdynowski.pl
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
